import { useCallback, useRef } from "react";
import TextFieldMUI from "@mui/material/TextField";
import Box from "@mui/material/Box";
import { Controller } from "react-hook-form";
import i18next from "i18next";
import Button from "../Buttons/Button";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {
  GRADIENT_PURPLE_FUCHSIA,
  GRADIENT_PURPLE_FUCHSIA_COLOR_TEXT,
  GRAY_DARK_THEME_COLOR,
} from "../../utils/colors";
import { useTranslationApp } from "../../lib/i18next";
import Typography from "@mui/material/Typography";
import { FIELD_REQUIRED } from "../../i18n/keysTranslations";
import { getReactHookFormNestedError } from "../../utils/errors";
import { textFieldInputSx } from "../../styles";

const NUMBER_SPIN_BUTTONS_SX = {
  "& input[type=number]": {
    MozAppearance: "textfield",
  },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    {
      WebkitAppearance: "none",
      margin: 0,
    },
};

const assignRef = (ref, element) => {
  if (!ref) {
    return;
  }
  if (typeof ref === "function") {
    ref(element);
    return;
  }
  ref.current = element;
};

const TextField = ({
  control,
  rules = {
    required: {
      value: true,
      message: i18next.t(FIELD_REQUIRED),
    },
  },
  id,
  label = "",
  variant = "standard",
  name = "",
  value = "",
  fullWidth = false,
  multiline = false,
  minRows,
  maxRows,
  helperText,
  onKeyDown,
  InputProps,
  disabled = false,
  onClick = () => {},
  actionsChange = () => {},
  actionsBlur = () => {},
  sx,
  placeholder = "",
  autoFocus = false,
  dataTour = "",
  type = "text",
  color = "primary",
  showCounterCharacters = false,
  maxCharacters = 0,
  InputLabelProps = {},
  className = "",
  hasButtonGenerate = false,
  useGradientButton = true,
  propsButtonGenerate = {},
  alignButtonGenerate = "top-right",
  errors,
  formValues = {},
  startIcon,
  inputProps,
  ...rest
}) => {
  // Obtener el valor del campo para el contador de caracteres
  const fieldValue = formValues?.[name] || value;

  const horizontalPosition = alignButtonGenerate.split("-")[1];
  const verticalPosition = alignButtonGenerate.split("-")[0];
  const alignItems = horizontalPosition === "right" ? "flex-end" : "flex-start";

  const hiddenLabel = variant === "filled" && !label;

  const { t } = useTranslationApp();

  const error = getReactHookFormNestedError(errors, name);
  const isNumberType = type === "number";
  const fieldRefRef = useRef(null);
  const callerWheelRef = useRef(inputProps?.onWheel);
  const wheelCleanupRef = useRef(null);
  callerWheelRef.current = inputProps?.onWheel;

  const setInputRef = useCallback(
    (element) => {
      if (wheelCleanupRef.current) {
        wheelCleanupRef.current();
        wheelCleanupRef.current = null;
      }

      if (element && type === "number") {
        const onWheel = (event) => {
          event.preventDefault();
          callerWheelRef.current?.(event);
        };
        element.addEventListener("wheel", onWheel, { passive: false });
        wheelCleanupRef.current = () => {
          element.removeEventListener("wheel", onWheel);
        };
      }

      assignRef(fieldRefRef.current, element);
    },
    [type]
  );

  // Si no hay control, no podemos usar Controller, retornar null
  if (!control) {
    return null;
  }

  const onGetSxButtonGenerate = () => {
    if (
      alignButtonGenerate === "top-right" ||
      alignButtonGenerate === "top-left"
    ) {
      const sxBase = {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      };
      if (alignButtonGenerate === "top-right") {
        return {
          ...sxBase,
          mr: 1,
        };
      } else {
        return {
          ...sxBase,
          ml: 1,
        };
      }
    }
    if (
      alignButtonGenerate === "bottom-right" ||
      alignButtonGenerate === "bottom-left"
    ) {
      const sxBase = {
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
      };
      if (alignButtonGenerate === "bottom-right") {
        return {
          ...sxBase,
          mr: 1,
        };
      } else {
        return {
          ...sxBase,
          ml: 1,
        };
      }
    }
  };

  const sxButtonGenerate = onGetSxButtonGenerate();
  const sxGradient = useGradientButton
    ? {
        background: GRADIENT_PURPLE_FUCHSIA,
        color: GRADIENT_PURPLE_FUCHSIA_COLOR_TEXT,
        "&.Mui-disabled": {
          backgroundColor: GRAY_DARK_THEME_COLOR,
        },
      }
    : {};
  const hasHelperText = error?.message || helperText || showCounterCharacters;

  const handleKeyDown = (event) => {
    if (isNumberType && (event.key === "e" || event.key === "E")) {
      event.preventDefault();
    }
    onKeyDown?.(event);
  };

  if (hasButtonGenerate) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems,
          width: "100%",
        }}
      >
        {verticalPosition === "top" && (
          <Button
            disabled={disabled}
            startIcon={<AutoAwesomeIcon />}
            size="small"
            sx={{
              ...sxButtonGenerate,
              ...sxGradient,
            }}
            type="button"
            loading={propsButtonGenerate.loading}
            onClick={propsButtonGenerate.onClick}
          >
            {t(propsButtonGenerate.text)}
          </Button>
        )}
        <Controller
          control={control}
          name={name}
          rules={rules}
          defaultValue={value}
          render={({ field }) => {
            fieldRefRef.current = field.ref;
            return (
              <TextFieldMUI
                id={id}
                hiddenLabel={hiddenLabel}
                className={className}
                color={color}
                data-tour={dataTour}
                autoFocus={autoFocus}
                label={label || ""}
                onClick={onClick}
                disabled={disabled}
                variant={variant}
                fullWidth={fullWidth}
                type={type}
                multiline={multiline}
                minRows={minRows}
                maxRows={maxRows}
                placeholder={placeholder}
                error={!!error}
                InputProps={{
                  ...InputProps,
                  startAdornment: startIcon || InputProps?.startAdornment,
                  endAdornment: InputProps?.endAdornment,
                }}
                {...field}
                {...rest}
                inputRef={setInputRef}
                inputProps={inputProps}
                onKeyDown={handleKeyDown}
                onChange={(e) => {
                  field.onChange(e);
                  actionsChange(e?.target?.value);
                }}
                onBlur={(e) => {
                  field.onBlur(e);
                  actionsBlur(e);
                }}
                sx={{
                  ...sx,
                  ...(isNumberType ? NUMBER_SPIN_BUTTONS_SX : {}),
                  ".MuiFormHelperText-root": {
                    mx: 0,
                  },
                }}
                InputLabelProps={InputLabelProps}
              />
            );
          }}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection:
              horizontalPosition === "right" ? "row-reverse" : "row",
            width: "100%",
          }}
        >
          {verticalPosition === "bottom" && (
            <Button
              disabled={disabled}
              startIcon={<AutoAwesomeIcon />}
              size="small"
              sx={{
                ...sxButtonGenerate,
                background: GRADIENT_PURPLE_FUCHSIA,
                color: GRADIENT_PURPLE_FUCHSIA_COLOR_TEXT,
                "&.Mui-disabled": {
                  backgroundColor: GRAY_DARK_THEME_COLOR,
                },
              }}
              type="button"
              loading={propsButtonGenerate.loading}
              onClick={propsButtonGenerate.onClick}
            >
              {t(propsButtonGenerate.text)}
            </Button>
          )}
          {hasHelperText && (
            <Box
              component="span"
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              {(error?.message || helperText) && (
                <Typography variant="caption" color="error">
                  {!!error ? error?.message : helperText}
                </Typography>
              )}
              {showCounterCharacters && (
                <Typography variant="caption">
                  {fieldValue?.length}/{maxCharacters}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      defaultValue={value}
      render={({ field }) => {
        fieldRefRef.current = field.ref;
        return (
          <TextFieldMUI
            id={name}
            hiddenLabel={hiddenLabel}
            className={className}
            color={color}
            data-tour={dataTour}
            autoFocus={autoFocus}
            label={label || ""}
            onClick={onClick}
            variant={variant}
            fullWidth={fullWidth}
            type={type}
            multiline={multiline}
            minRows={minRows}
            maxRows={maxRows}
            placeholder={placeholder}
            error={!!error}
            helperText={
              <Box
                component="span"
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <Typography variant="caption">
                  {!!error ? error?.message : helperText}
                </Typography>
                {showCounterCharacters && (
                  <Typography
                    variant="caption"
                    color={
                      field?.value?.length > maxCharacters
                        ? "error"
                        : "fields.placeholder"
                    }
                  >
                    {field?.value?.length}/{maxCharacters}
                  </Typography>
                )}
              </Box>
            }
            InputProps={{
              ...InputProps,
              startAdornment: startIcon || InputProps?.startAdornment,
              endAdornment: InputProps?.endAdornment,
            }}
            {...field}
            {...rest}
            disabled={disabled}
            inputRef={setInputRef}
            inputProps={inputProps}
            onKeyDown={handleKeyDown}
            onChange={(e) => {
              field.onChange(e);
              actionsChange(e.target.value);
            }}
            onBlur={(e) => {
              field.onBlur(e);
              actionsBlur(e);
            }}
            sx={{
              ...textFieldInputSx,
              ...sx,
              ...(isNumberType ? NUMBER_SPIN_BUTTONS_SX : {}),
            }}
            InputLabelProps={InputLabelProps}
          />
        );
      }}
    />
  );
};

export default TextField;
